import React, { useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  TextField,
  Tooltip,
} from "@mui/material";
import { Add } from "@mui/icons-material";
import { observer } from "mobx-react";
import ReceiptDataEntryDialog from "../../PatientDataEntryForm/Receipt";
import QuickAddRxDialog from "./QuickAddRxDialog";
import { useMasterTables } from "../../../stores/MasterTables/masterTables.queries";
import { usePatient } from "../../../stores/Patients/patient.queries";
import useStore from "../../../helpers/useStore";
import { PatientsContext } from "../../../stores/Patients/patients.provider";
import { UserContext } from "../../../stores/User/User.provider";
import { QuickRxData, RxType } from "./RxOverView";
import { v4 as uuidv4 } from "uuid";
import { SelectedRow } from "../../PatientDataEntryForm/PatientPage";
import { validateTherapy, validateFrequency } from "../../../molecules/Editable";
import notificationStore from "../../../stores/Notification/notificationStore";
import { useSaveRxs } from "../../../stores/Rx/rx.mutations";
import { toQuickRxWriteLine, QuickRxWriteLine } from "./quickRxLines";
import type { RxWriteDto } from "../../../api-client";
import { usePrescribedTherapyOptions } from "../../../stores/MasterTables/masterTables.queries";

interface NewRxCardProps {
  onNewRxClick?: () => void;
  onRxCreated?: () => void; // Callback when a new RX is successfully created
}

const NewRxCard: React.FC<NewRxCardProps> = observer(({ onNewRxClick, onRxCreated }) => {
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [onNewRx, setOnNewRx] = useState(false);
  const [isQuickAdd, setIsQuickAdd] = useState(false);
  const [quickRxData, setQuickRxData] = useState<QuickRxData>({
    rxTypeData: { id: "", text: "" },
    selectedTarifGroup: "",
    rxAccount: "",
    tableData: [],
  });
  const [selectedRow, setSelectedRow] = useState<SelectedRow | null>(null);

  // Get required data from stores
  const { selectedPatientId } = useStore(PatientsContext);
  // Prescribed TERMS — the catalog level a prescription names (MD-232).
  const { data: termOptions = [] } = usePrescribedTherapyOptions();
  const { user } = useStore(UserContext);
  const { data: patientData } = usePatient(selectedPatientId!);
  const { data: masterData } = useMasterTables();
  const { mutate: patientSaveRxs } = useSaveRxs();

  const insurer = (masterData || [])
    .filter((obj: any) => "insurer" in obj)
    .map((obj: any) => obj.insurer);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      setIsQuickAdd(true);
    }
  };

  // const handleAddClick = () => {
  //   setOnNewRx(true);
  //   onNewRxClick?.();
  // };

  const handleAddClick = () => {
    const row = {
      id: uuidv4(),
      isHomeVisit: null,
      therapy: "",
      amount: "",
      frequency: "",
    };
    setSelectedRow(row);
    const formattedValues = {
      ...patientData,
      rxs: [...(patientData?.rxs ?? []), row], // Providing an empty array as a fallback if patientData?.rxs is undefined
    };
    setOnNewRx(true);
    onNewRxClick?.();
  };
  const handleCloseDialog = () => {
    setOnNewRx(false);
  };

  const handleEditClose = () => {
    setInputValue("");
    setQuickRxData({
      rxTypeData: { id: "", text: "" },
      selectedTarifGroup: "",
      rxAccount: "",
      tableData: [],
    });
    setIsQuickAdd(false);
  };

  const handleSave = async () => {
    // console.log("Handling save", quickRxData);
    const { rxTypeData, selectedTarifGroup, rxAccount, tableData } = quickRxData || {};
    try {
      if (!rxTypeData) {
        notificationStore.showMessage("Bitte wählen Sie Rezept-typ, um fortzufahren.", "warning");
        return;
      }
      if (!selectedTarifGroup) {
        notificationStore.showMessage(
          "Bitte wählen Sie die Tarifgruppe, um fortzufahren.",
          "warning"
        );
        return;
      }
      if (!rxAccount) {
        notificationStore.showMessage(
          "Bitte wählen Sie ein Rezeptkonto aus, um fortzufahren.",
          "warning"
        );
        return;
      }
      // setSavingRx(true);
      // Typed, not `any[]`: an `any` here is exactly what let the pre-MD-232
      // line shape reach the server unchallenged.
      const therapyRxs: QuickRxWriteLine[] = [];
      const filteredTherapy = (tableData || []) // Ensure formData.therapyRxs is an array or provide an empty array
        .filter((therapy: any) => !!therapy.therapy);

      // Loop through tableData to collect therapy data
      for (const therapy of filteredTherapy) {
        // MD-232: a line names the prescribed TERM and nothing else, so the
        // Heilmittel travels as the text that was typed and the server resolves
        // it through the alias table.
        const therapyObj = toQuickRxWriteLine(therapy);

        const validTherapy = validateTherapy(therapyObj.rawText!, masterData || [], termOptions);
        const validFrequency = validateFrequency(therapyObj.frequency.id!, masterData || []);
        if (!validTherapy || !validFrequency) {
          throw new Error("Bitte geben Sie eine gültige Therapie und Häufigkeit ein.");
        }
        therapyRxs.push(therapyObj);
      }
      if (therapyRxs.length === 0) {
        notificationStore.showMessage(
          "Bitte fügen Sie mindestens ein Heilmittel hinzu, um fortzufahren.",
          "warning"
        );
        return;
      }
      const selectedPayer: any = insurer[0].find(
        (option: any) =>
          option.id === (patientData?.payer as any)?.id ||
          option.ik === (patientData?.payer as any)?.id
      );

      // Construct the Rx object with therapyRxs array.
      // `satisfies` rather than a plain literal: this is the payload the server
      // has to accept, so it is checked against the WRITE contract here, at the
      // point it is built, instead of being silently reshaped on the way out.
      const rxData = {
        Receiptpayer: (patientData?.payer as any)?.id,
        insuranceStatus: patientData?.status,
        insureeNumber: patientData?.insuranceNo,
        rxtype: rxTypeData,
        accountId: rxAccount,
        tarifGroupId:
          selectedTarifGroup || patientData?.tarifGroupId || selectedPayer?.tarifGroup?.id,
        isHomeVisit: therapyRxs[0].isHomeVisit ?? undefined,
        therapyRxs: therapyRxs,
      } satisfies RxWriteDto;

      // console.log("Updatede data:", [rxData], therapyRxs, tableData);

      patientSaveRxs({ patientId: patientData?.id!, rxs: [rxData] });
      setQuickRxData({
        rxTypeData: { id: "", text: "" },
        selectedTarifGroup: "",
        rxAccount: "",
        tableData: [],
      });

      setIsQuickAdd(false);
      setInputValue("");
    } catch (error: any) {
      notificationStore.showMessage(error.message || "Fehler beim Speichern des Rezepts", "error");
    }
  };

  // Mock grid columns for QuickAddRxDialog - this should be moved to proper location
  const gridColumns = [
    // { field: "therapy", headerName: "Therapy", width: 150 },
    { field: "amount", headerName: "Amount", width: 100 },
    { field: "frequency", headerName: "Frequency", width: 150 },
  ];

  return (
    <>
      <Card sx={{ marginBottom: 2, padding: "0px" }}>
        <CardHeader title="Neues Rezept" />
        <CardContent
          sx={{
            padding: "0px",
            border: "0px solid green",
            paddingBottom: "4px !important",
          }}
        >
          <Box
            sx={{
              padding: "4px",
              display: "flex",
              gap: "10px",
              alignItems: "center",
            }}
          >
            <Tooltip title="6 KG HB 2x or 6 KG HB 8 KGG 1-2 x or 6 KG 2x">
              <TextField
                disabled={isLoading}
                type="text"
                label={
                  isLoading ? (
                    <Box display="flex" alignItems="center">
                      <CircularProgress size={20} sx={{ marginRight: 1 }} />
                      Loading...
                    </Box>
                  ) : (
                    "Schnelltext"
                  )
                }
                placeholder="6 KG HB 2x or 6 KG HB 8 KGG 1-2 x or 6 KG 2x"
                sx={{
                  padding: 0,
                  borderRadius: "4px",
                  backgroundColor: "white",
                }}
                value={inputValue}
                InputLabelProps={{ style: { fontWeight: "bold" } }}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
              />
            </Tooltip>

            <Button onClick={handleAddClick} disabled={isLoading}>
              <Add />
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Quick Add Dialog */}
      {isQuickAdd && (
        <QuickAddRxDialog
          open={isQuickAdd}
          onClose={handleEditClose}
          onSave={handleSave}
          user={user}
          gridColumns={gridColumns}
          isQuickAdd={isQuickAdd}
          initialInputValue={inputValue}
          setQuickRxData={setQuickRxData}
          rxData={quickRxData}
          masterData={masterData}
        />
      )}

      {/* Receipt Data Entry Dialog */}
      {onNewRx && masterData && patientData && (
        <ReceiptDataEntryDialog
          openDialog={onNewRx}
          handleCloseDialog={handleCloseDialog}
          patientValues={patientData}
          selectedRow={selectedRow}
        />
      )}
    </>
  );
});

export default NewRxCard;
